/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/// <reference types="ckeditor" />
import { CKEditor4, CKEditorComponent } from "ckeditor4-angular";
import { LogHelper } from "../../logging/log-helper";

const log = new LogHelper('CKEditorHelper');

/**
 * Sometimes the Angular integration with CKEditor fails to properly cleanup an editor
 * instance when the parent component is destroyed. This causes problems on subsequent
 * loads. See: https://github.com/ckeditor/ckeditor4/issues/4390 This helper class ensures 
 * that we cleanup properly and prevent this issue.
 * 
 * We make a best effort to not destroy editor instances that we do not own.
 */
export class CKEditorHelper {
  
  public static readonly defaultHeight: number = 391; // bar=42 + content=320 + bar=27 + border=2 == 391
  public static readonly defaultEditorUrl: string = 'https://cdn.alertsense.com/res/ckeditor/v20250324/ckeditor.js';

  // see: https://ckeditor.com/docs/ckeditor4/latest/api/CKEDITOR_config.html
  // see also: https://ckeditor.com/docs/ckeditor4/latest/guide/dev_angular.html
  public static readonly defaultConfig: CKEditor4.Config = {
    // default config
    uiColor: '#cdcdcd',
    height: '320',
    language: 'en',
    enterMode: 2,
    versionCheck: false,
    allowedContent: true,
    fillEmptyBlocks: false,

    toolbarGroups: [
      { name: 'styles', groups: ['styles'] },
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi', 'paragraph'] },
      { name: 'colors', groups: ['colors'] },
      { name: 'insert', groups: ['insert'] },
      { name: 'document', groups: ['mode', 'document', 'doctools'] },
      { name: 'links', groups: ['links'] },
    ],

    extraPlugins: [],
    removePlugins: [],
  }

  public static cleanup(component: CKEditorComponent, editorId: string) {
    if (typeof CKEDITOR === 'undefined') return;
    const instance = component?.instance as CKEDITOR.editor;
    editorId = editorId ?? instance?.name;

    // first check to see if the component has an editor instance attached
    if (this.safeDestroy(instance)) return;

    const instances = CKEDITOR?.instances;
    if (!instances || typeof instances !== 'object' || !Object.keys(instances).length) return;

    // next see if the global instances collection has an instance with our editorId
    if (this.safeDestroy(instances[editorId])) return;

    // as a last resort, destroy all instances in the global collection
    log.warn(`No instance found for editorId=${editorId}. Destroying all CKEditor instances as a last resort.`);
    Object.keys(instances).forEach(id => {
      this.safeDestroy(instances[id]);
    });
  }

  private static safeDestroy(instance: CKEDITOR.editor): boolean {
    if (!instance || typeof instance !== 'object') return false;
    if (!('destroy' in instance) || typeof instance.destroy !== 'function') return false;

    log.debug(`destroy instance with id=${instance.name}`);

    try {
      instance.destroy();
      return true
    } catch (e) {
      log.warn(`Error destroying CKEditor instance (name=${instance?.name})`, e);
      return false;
    }
  }

  public static getEditor(editorName: string): CKEDITOR.editor | null {
    if (typeof CKEDITOR === 'undefined') return null;
    return CKEDITOR.instances[editorName];
  }

  public static getEditorDoc(editorName: string): CKEDITOR.dom.document | null {
    return this.getEditor(editorName)?.document;
  }

  public static findElement(editorName: string, selector: string): CKEDITOR.dom.element | null {
    return this.getEditorDoc(editorName)?.findOne(selector);
  }

  public static findElementsByTag(editorName: string, tag: string): CKEDITOR.dom.element[] {
    if (typeof CKEDITOR === 'undefined') return [];
    const nodes = this.getEditorDoc(editorName)?.getElementsByTag(tag);
    const elements: CKEDITOR.dom.element[] = [];
    if (!nodes) return elements;
    for (let i = 0; i < nodes.count(); i++) {
      const node = nodes.getItem(i);
      let element: CKEDITOR.dom.element;
      if (node.type === CKEDITOR.NODE_ELEMENT) {
        element = node as CKEDITOR.dom.element;
      } else {
        element = node.getParent();
      }
      if (element) elements.push(element);
    }
    return elements;
  }

  public static modifyElement(editorName: string, selector: string, action?: (el: CKEDITOR.dom.element) => void): CKEDITOR.dom.element {
    const el = this.findElement(editorName, selector);
    if (el && action && typeof action === 'function') {
      action(el);
    }
    return el;
  }

  public static openImageDialog(editorName: string, imgEl: CKEDITOR.dom.element): void {
    const editor = this.getEditor(editorName);
    if (editor) {
      editor.getSelection().selectElement(imgEl);

      // Listen for dialog show event to focus alt text
      const dialogShowHandler = (evt: any) => {
        const dialog = evt.data;
        if (dialog.getName() === 'image') {
          // Focus the alt text input after dialog is fully rendered
          setTimeout(() => {
            const altTextInput = dialog.getContentElement('info', 'txtAlt');
            if (altTextInput) {
              altTextInput.focus();
            }
          }, 100);

          // Remove the event listener after use
          editor.removeListener('dialogShow', dialogShowHandler);
        }
      };

      // Add the event listener
      editor.on('dialogShow', dialogShowHandler);

      // Trigger the image dialog
      if (editor.plugins.image) {
        editor.fire('doubleclick', {
          element: imgEl
        });
      }
    }
  }

  public static getDialog(targetDialog: string): CKEDITOR.dialog {
    if (typeof CKEDITOR === 'undefined') return null;
    if (CKEDITOR.dialog) {
      const currentDialog = CKEDITOR.dialog.getCurrent();
      if (currentDialog?.getName() === targetDialog)
        return currentDialog;
    }
    return null;
  }

  public static trySetDialogInputValue(dialog: CKEDITOR.dialog, page: string, elementName: string, value: string): void {
    if (dialog) {
      const element = dialog.getContentElement(page, elementName) as any;
      if (element) {
        element.setValue(value);
      }
    }
  }

  public static readonly languageMap: { [key: string]: string } = {
    'af': 'af',
    'ar': 'ar',
    'az': 'az',
    'bg': 'bg',
    'bn': 'bn',
    'bs': 'bs',
    'ca': 'ca',
    'cs': 'cs',
    'cy': 'cy',
    'da': 'da',
    'de-CH': 'de-ch',
    'de': 'de',
    'el': 'el',
    'en-AU': 'en-au',
    'en-CA': 'en-ca',
    'en-GB': 'en-gb',
    'en': 'en',
    'eo': 'eo',
    'es-MX': 'es-mx',
    'es': 'es',
    'et': 'et',
    'eu': 'eu',
    'fa': 'fa',
    'fi': 'fi',
    'fo': 'fo',
    'fr-CA': 'fr-ca',
    'fr': 'fr',
    'gl': 'gl',
    'gu': 'gu',
    'he': 'he',
    'hi': 'hi',
    'hr': 'hr',
    'hu': 'hu',
    'id': 'id',
    'is': 'is',
    'it': 'it',
    'ja': 'ja',
    'ka': 'ka',
    'km': 'km',
    'ko': 'ko',
    'ku': 'ku',
    'lt': 'lt',
    'lv': 'lv',
    'mk': 'mk',
    'mn': 'mn',
    'ms': 'ms',
    'nb': 'nb',
    'nl': 'nl',
    'no': 'no',
    'oc': 'oc',
    'pl': 'pl',
    'pt-BR': 'pt-br',
    'pt': 'pt',
    'ro': 'ro',
    'ru': 'ru',
    'si': 'si',
    'sk': 'sk',
    'sl': 'sl',
    'sq': 'sq',
    'sr-Latn': 'sr-latn',
    'sr-RS': 'sr-latn',
    'sr': 'sr',
    'sv': 'sv',
    'th': 'th',
    'tr': 'tr',
    'tt': 'tt',
    'ug': 'ug',
    'uk': 'uk',
    'vi': 'vi',
    'vi-VI': 'vi',
    'zh-CN': 'zh-cn',
    'zh-SG': 'zh-cn',
    'zh': 'zh',
    'zh-TW': 'zh',
    'zh-HK': 'zh',
  };
}